import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import {
  ActionSheetMenuOption,
  useActionSheetModal,
} from 'components/Modal/ActionSheetModal/ActionSheetModal'
import useGlobalConfirmBox from 'features/GlobalConfirmBox/hooks/useGlobalConfirmBox'
import { Message, MessageType } from 'lib/_types'

export const DELETEABLE_TYPE: Readonly<MessageType[]> = [
  'text',
  'image',
  'eternal_image',
  'video',
  'eternal_video',
  'gif',
  'sticker',
  // 'screenshot_warning',
  // 'call',
  // 'individual_call',
  // 'owner_kick',
  // 'user_joined',
  // 'user_leave',
  // 'deleted',
]

export const COPYABLE_TYPE: Readonly<MessageType[]> = [
  'text',
  // 'image',
  // 'eternal_image',
  // 'video',
  // 'eternal_video',
  // 'gif',
  // 'sticker',
  // 'screenshot_warning',
  // 'call',
  // 'individual_call',
  // 'owner_kick',
  // 'user_joined',
  // 'user_leave',
  // 'deleted',
]

export const REPORTABLE_TYPE: Readonly<MessageType[]> = [
  'text',
  'image',
  'eternal_image',
  'video',
  'eternal_video',
  'gif',
  'sticker',
  // 'screenshot_warning',
  // 'call',
  // 'individual_call',
  // 'owner_kick',
  // 'user_joined',
  // 'user_leave',
  // 'deleted',
]

export const REPLYABLE_TYPE: Readonly<MessageType[]> = [
  'text',
  'image',
  'eternal_image',
  'video',
  'eternal_video',
  'gif',
  'sticker',
  // 'screenshot_warning',
  // 'call',
  // 'individual_call',
  // 'owner_kick',
  // 'user_joined',
  // 'user_leave',
  // 'deleted',
]

type Props = {
  message: Message
  isCurrentUserMessage: boolean
  onDeleteMessage: (messageId: number) => void
  onReport: (userId: number) => void
  onReply: (message: Message) => void
}

export const useChatMessageMenu = ({
  message,
  isCurrentUserMessage,
  onDeleteMessage,
  onReport,
  onReply,
}: Props) => {
  const { t } = useTranslation()
  const { showConfirmBox } = useGlobalConfirmBox()
  const { open } = useActionSheetModal()

  const menu = useMemo<ActionSheetMenuOption[]>(() => {
    if (!message) return []

    const isReplyable = REPLYABLE_TYPE.includes(message.message_type)
    const isCopyable = COPYABLE_TYPE.includes(message.message_type)
    const isDeletable =
      isCurrentUserMessage && DELETEABLE_TYPE.includes(message.message_type)
    const isReportable =
      !isCurrentUserMessage && REPORTABLE_TYPE.includes(message.message_type)

    const result: (ActionSheetMenuOption | false)[] = [
      isReplyable && {
        title: t('common.reply'),
        iconPath: '/images/icon-reply-black.svg',
        onClick: () => {
          onReply(message)
        },
      },
      isCopyable && {
        title: t('copy_to_clipboard.copy'),
        iconPath: '/images/icon-copy-black.svg',
        onClick: () => {
          navigator?.clipboard?.writeText(message.text ?? '')
        },
      },
      isDeletable && {
        title: t('common.delete'),
        iconPath: '/images/icon-delete-red.svg',
        isDestructiveAction: true,
        onClick: () => {
          showConfirmBox({
            title: t('chat.message.delete.title'),
            content: t('chat.message.delete.content'),
            confirmBoxProps: {
              confirmButtonStyle: 'red',
              confirmButtonText: t('common.delete'),
              onOk: async () => {
                await onDeleteMessage(message.id)
              },
            },
          })
        },
      },
      isReportable && {
        title: t('common.report'),
        iconPath: '/images/icon-report-red.svg',
        isDestructiveAction: true,
        onClick: () => {
          if (message.user_id) onReport(message.user_id)
        },
      },
    ]

    return result.filter((x) => x) as ActionSheetMenuOption[]
  }, [message])

  const openActionSheetMenu = () => {
    open({ menu })
  }

  return {
    menu,
    openActionSheetMenu,
  }
}
